import { useState, memo, useEffect } from 'react';
import { makeStyles } from '@material-ui/styles';
import theme from '../../theme';
import DesktopMenuItem from './desktopMenuItem';
import { useTranslation } from '@tools/i18n';
import { useMeasure } from 'react-use';
import { useRouter } from 'next/router';
import { Typography } from '@material-ui/core';
import { ChevronRight } from '@material-ui/icons';
import Link from 'next/link';
import urlCorrection from '@tools/urlCorrection';
import useSessionStore from '@services/stores/SessionStore';
import useApplicationStore from '@services/stores/ApplicationStore';

const styles = {
    navigation__wrapper: {
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        maxWidth: theme.maxWidth,
        margin: '0 auto',
        gridColumn: '1 / 4',
        gridRow: 2,
        '&.open:after': {
            height: 'calc(100vh - 100%)',
            opacity: 1
        },

        [theme.breakpoints.down('sm')]: {
            display: 'none'
        }
    },
    navigation__inner: {
        position: 'relative',
        width: '100%',
        maxWidth: theme.maxWidth,
        display: 'flex',
        padding: '0 15px',
        gap: 18,
        [theme.breakpoints.down('md')]: {
            overflowX: 'auto',
            '&::-webkit-scrollbar': {
                width: 0,
                background: 'transparent',
            },
        },
        [theme.breakpoints.up('lg')]: {
            padding: '0 0',
        },
    },
    navigation__list: {
        display: 'flex',
        justifyContent: 'center',
        position: 'relative',
        width: '100%',
        padding: 0,
        margin: 0,
        listStyle: 'none',
        '&.left': {
            justifyContent: 'flex-start'
        },
        '&.right': {
            justifyContent: 'flex-end',
            marginLeft: 'auto',
            marginRight: 0,
        },
        [theme.breakpoints.up('md')]: {
            //flexWrap: 'wrap',
            width: 'auto',
        },
        [theme.breakpoints.up('lg')]: {
            flexWrap: 'nowrap',
            //width: '100%',
        },
    },
    navigation__item: {
        fontSize: '12px',
        '& a, & span': {
            display: 'block',
            padding: '14px 18px',
            textDecoration: 'none',
            color: theme.palette.header.menu.items.level0,
            fontFamily: theme.typography.headerFont,
            fontSize: 14,
            whiteSpace: 'nowrap',
            [theme.breakpoints.down('md')]: {
                padding: '14px min(10px, 0.7vw)'
            },
        },
        '.left > &:first-of-type a': {
            paddingLeft: 0,
        },
        '.right > &:last-of-type a': {
            paddingRight: 0,
        },
        '&.level-1 > a, &.level-1 > span': {
            color: theme.palette.header.menu.items.level0,
        },
        '&.level-2 > a, &.level-2 > span': {
            color: theme.palette.header.menu.items.level2,
        },
        '&.level-2 > a': {
            color: theme.palette.header.menu.items.level2,
            fontWeight: 400
        },
        '&.level-3 > a': {
            textTransform: 'none',
            color: theme.palette.header.menu.items.level3,
            transition: 'padding 0.25s ease',
            '&:hover': {
                padding: '0 0 0 10px'
            }
        }
    },
    bottomlink: {
        '&.account a': {
            display: 'flex',
            justifyContent: 'space-between',
            gap: '15px',
            flexDirection: 'row',
        },
    },
    navigation__subAnimate: {
        position: 'fixed',
        top: '100%',
        width: '100%',
        height: 'auto',
        left: 0,
        zIndex: 2000,
        opacity: 0,
        overflow: 'hidden',
        backgroundColor: theme.palette.header.menu.background,
        translate: '0 -20px',
        scale: 0,
        transformOrigin: 'top center',
        transition: 'translate 0.3s cubic-bezier(.17,.67,.59,1.05), opacity 0.25s cubic-bezier(.17,.67,.59,1.05)',
        paddingBottom: 20,
        boxShadow: '0px 95px 70px 10px rgba(0,0,0,0.1)',
        '.open &': {
            opacity: 1,
            translate: '0 0',
            scale: 1,
        }

    },
    navigation__subScroll: {
        overflow: 'auto',
        maxHeight: 'calc(100vh - 190px)'
    },
    navigation__subMenu: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        width: '100%',
        background: theme.palette.header.menu.background,
    },
    navigation__subList: {
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)',
        gridRowGap: 20,
        padding: '10px 15px 30px',
        margin: 0,
        width: '100%',
        maxWidth: theme.maxWidth,
        listStyle: 'none',
        [theme.breakpoints.up('lg')]: {
            padding: '20px 0 10px'
        },
        '&.level-2': {
            display: 'flex',
            flexDirection: 'column',
            gridRowGap: 0,
            padding: '10px 0 0'
        },
        '& li a, & li span': {
            padding: '0',
            fontSize: 13,
            lineHeight: '24px'
        },
        '&.level2-only': {
            gridTemplateColumns: '1fr',
            gridRowGap: 0,
            '& ul': {
                display: 'none'
            },
            '& a': {
                padding: 0,
                color: theme.palette.header.menu.altColor,
                textTransform: 'none'
            }
        }
    },
    burger__trigger: {
        display: 'none',
    },
    navigation__seeAll: {
        display: 'flex',
        padding: '20px 15px 10px',
        justifyContent: 'flex-start',
        width: '100%',
        maxWidth: '1600px',
        [theme.breakpoints.up('lg')]: {
            padding: '20px 0 10px'
        },
        '& > a': {
            color: '#222',
            textDecoration: 'none',
            fontFamily: theme.typography.headerFont,
            fontWeight: 500,
        }
    },
    navigation__showMore: {
        display: 'flex',
        alignItems: 'center',
        lineHeight: '24px',
        fontSize: 12,
        textTransform: 'none',
        cursor: 'pointer',
        color: theme.palette.header.menu.items.level2,
    },
    obscure: {
        content: '""',
        position: 'fixed',
        zIndex: 99,
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        pointerEvents: 'none',
        opacity: 0,
        backgroundColor: 'rgba(0,0,0,0.4)',
        transition: 'opacity .1s',
        transitionTimingFunction: 'ease-in',
        '&.focus': {
            opacity: 1,
            pointerEvents: 'auto',
        }
    }
};

const useStyles = makeStyles(() => styles);

function DesktopMenu({ menu }) {
    const classes = useStyles();
    const router = useRouter();
    const { t } = useTranslation('general');
    const salesChannel = useSessionStore((state) => state.session.salesChannel);
    const { searchOpen } = useApplicationStore((state) => state.modals)
    const [state, setState] = useState({
        open: false,
        itemHover: false,
        subHover: false,
        submenuItems: [],
        showMore: [],
        parent: null,
    });
    const [ref, { height }] = useMeasure();

    const {
        translated: {
            customFields: {
                menu_top_links_item_one_text,
                menu_top_links_item_one_url,
                menu_top_links_item_two_text,
                menu_top_links_item_two_url,
                menu_top_links_item_three_text,
                menu_top_links_item_three_url,
                menu_bottom_links_item_one_text,
                menu_bottom_links_item_one_url,
                menu_bottom_links_item_three_text,
                menu_bottom_links_item_three_url,
                menu_bottom_links_item_two_text,
                menu_bottom_links_item_two_url
            }
        }
    } = salesChannel;


    useEffect(() => {
        const handleStart = url => {
            setState({ ...state, open: false });
        };

        router.events.on('routeChangeStart', handleStart);

        return () => {
            router.events.off('routeChangeStart', handleStart);
        };
    }, [router]);

    // Events bubbling up from MenuItem
    function onHoverItem(children, parent) {
        if (children.length === 0 || searchOpen) {
            return;
        }
        setState(state => ({ ...state, open: true, itemHover: true, submenuItems: children, parent }));
    }

    function onLeaveItem(e) {
        let open = state.open;

        if (state.subHover) {
            open = true;
        } else {
            open = false;
        }
        setState(state => ({ ...state, open: open, itemHover: false }));
    }

    // Events from the submenu
    function onHoverSub() {
        setState(state => ({ ...state, open: true, subHover: true }));
    }

    function onLeaveSub(e) {
        let open = state.open;

        if (state.itemHover) {
            open = true;
        } else {
            open = false;
        }

        setState(state => ({ ...state, open: open, subHover: false }));
    }

    function toggleShowMore(id) {
        let showMore = state.showMore;
        showMore.push(id);
        setState(state => ({ ...state, showMore }));
    }

    function is2Level(children) {
        let childrenHasChildren = children.length > 0 ? false : true;
        children.map((subCategory) => {
            if(subCategory.children.length > 0) {
                childrenHasChildren = true;
            }
        });

        return !childrenHasChildren;
    }

    // Renders navigation link tree
    function renderItems(items, level, parent) {
        let renderedItems = [];

        if (level === 3 && parent.type !== 'folder') {
            // console.log(parent);
            renderedItems.push(
                <DesktopMenuItem
                    item={parent}
                    level={level}
                    classes={{
                        item: classes.navigation__item,
                        subMenu: classes.navigation__subMenu,
                        subList: classes.navigation__subList
                    }}
                    open={state.open}
                    label={t('see-all')}
                    onHover={onHoverItem}
                    onLeave={onLeaveItem}
                    key={parent.id + '-' + level + '/' + 0}
                />
            );
        }

        (items || []).forEach((item, index) => {
            if (item?.name === 'Nyheter' || item?.name === 'News' || item?.name === 'Neuheiten' || item?.name === 'Nieuw binnen' || item?.name === 'Nyheder' || item?.name === 'Uutuudet') {
                return renderedItems
            }
            if (level < 3 || (level >= 3 && item && state.showMore.indexOf(item.parentId) >= 0) || index < 8) {
                renderedItems.push(
                    <DesktopMenuItem
                        item={item}
                        level={level}
                        classes={{
                            item: classes.navigation__item,
                            subMenu: classes.navigation__subMenu,
                            subList: classes.navigation__subList
                        }}
                        open={state.open}
                        onHover={onHoverItem}
                        onLeave={onLeaveItem}
                        key={`${item.id || item.name}-${level}/${index}`}
                    >
                        {renderItems(item.children, level + 1, item)}
                    </DesktopMenuItem>
                );
            } else if(index === 8 && items.length > 8) {
                renderedItems.push(
                    <Typography className={classes.navigation__showMore}
                                onClick={toggleShowMore.bind(this, parent.id)}
                                key={`${index}-${item.id}`}>
                        {t('show-more')}
                        <ChevronRight style={{ fontSize: 16 }}/>
                    </Typography>
                );
            }
        });

        return renderedItems;
    }

    function renderToplinks() {
        return (
            <>
                {menu_top_links_item_one_text && menu_top_links_item_one_url && (
                    <li className={[classes.navigation__item, classes.toplink].join(' ')}>
                        <Link href={menu_top_links_item_one_url}>
                            <a className={'link'}>{menu_top_links_item_one_text}</a>
                        </Link>
                    </li>
                )}

                {menu_top_links_item_two_text && menu_top_links_item_two_url && (
                    <li className={[classes.navigation__item, classes.toplink].join(' ')}>
                        <Link href={menu_top_links_item_two_url}>
                            <a className={'link'}>{menu_top_links_item_two_text}</a>
                        </Link>
                    </li>
                )}

                {menu_top_links_item_three_text && menu_top_links_item_three_url && (
                    <li className={[classes.navigation__item, classes.toplink].join(' ')}>
                        <Link href={menu_top_links_item_three_url}>
                            <a className={'link'}>{menu_top_links_item_three_text}</a>
                        </Link>
                    </li>
                )}
                <span className={[classes.toplink, 'last-space'].join(' ')}></span>
            </>
        );
    }

    function renderBottomlinks() {
        return (
            <>
                <span className={[classes.bottomlink, 'first-space'].join(' ')}></span>

                {menu_bottom_links_item_one_text && menu_bottom_links_item_one_url && (
                    <li className={[classes.navigation__item, classes.bottomlink].join(' ')}>
                        <Link href={menu_bottom_links_item_one_url}>
                            <a className={'link'}>{menu_bottom_links_item_one_text}</a>
                        </Link>
                    </li>
                )}

                {menu_bottom_links_item_two_text && menu_bottom_links_item_two_url && (
                    <li className={[classes.navigation__item, classes.bottomlink].join(' ')}>
                        <Link href={menu_bottom_links_item_two_url}>
                            <a className={'link'}>{menu_bottom_links_item_two_text}</a>
                        </Link>
                    </li>
                )}

                {menu_bottom_links_item_three_text && menu_bottom_links_item_three_url && (
                    <li className={[classes.navigation__item, classes.bottomlink].join(' ')}>
                        <Link href={menu_bottom_links_item_three_url}>
                            <a className={'link'}>{menu_bottom_links_item_three_text}</a>
                        </Link>
                    </li>
                )}
            </>
        );
    }
    function renderSeeAllLink() {
        if (!state?.parent?.url) {
            return null;
        }

        let label='';
        if (state.submenuItems.length > 0) {
            label = t('general:menu-see-all-mega-desktop', {
                categoryName: state?.parent?.name
            });
        }

        const url = urlCorrection(state?.parent?.url);

        return <div className={classes.navigation__seeAll}>
            <Link href={url}>
                <a>
                    { label }
                </a>
            </Link>
        </div>
    }

    function renderNav() {
        return (
            <nav className={[classes.navigation__wrapper, state.open ? 'open' : '', 'navigation'].join(' ')}>
                <div className={classes.navigation__inner}>
                    <ul className={[classes.navigation__list, 'left'].join(' ')}>
                        {renderToplinks()}
                    </ul>
                    <ul className={[classes.navigation__list, 'left'].join(' ')}>
                        {renderItems(menu, 1)}
                    </ul>

                    <ul className={[classes.navigation__list, 'right'].join(' ')}>
                        {renderBottomlinks()}
                    </ul>

                    <div
                        className={classes.navigation__subAnimate}
                        onMouseEnter={onHoverSub}
                        onMouseLeave={onLeaveSub}
                    >
                        <div className={classes.navigation__subScroll}>
                            <div className={classes.navigation__subMenu} ref={ref}>
                                {renderSeeAllLink()}
                                {state.submenuItems && state.submenuItems.length > 0 && (
                                    <ul className={[classes.navigation__subList, is2Level(state.submenuItems) ? 'level2-only' : ''].join(' ')}>
                                        {renderItems(state.submenuItems, 2)}
                                    </ul>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </nav>
        );
    }

    return <>{renderNav()}</>;
}

export default memo(DesktopMenu);
